import telegramLogo from '../../shared/assets/logos/telegram_logo.svg';
import whatsappLogo from '../../shared/assets/logos/whatsapp_logo.svg';
import viberLogo from '../../shared/assets/logos/viber_logo.svg';
import noAvatar from '../../shared/assets/images/no_avatar.svg';
import { useMe } from '../Area/Area';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import {
  fetchAllUsers,
  fetchUserById,
  selectUsersStatus,
} from '../../entities/user/model';
import { useEffect } from 'react';
import { User } from '../../types/interfaces';

const ManagerBlock = ({ manager, label }: { manager: any; label: string }) => (
  <div className="flex gap-[10px] justify-between items-center">
    <img
      src={manager?.photo || noAvatar}
      alt={`${manager?.first_name ?? ''} ${manager?.last_name ?? ''}`}
      className="w-16 h-16 rounded-full object-cover flex-shrink-0"
      onError={(e) => { (e.target as HTMLImageElement).src = noAvatar; }}
    />
    <div className="flex flex-col gap-[10px]">
      <p className="font-semibold">{label}</p>
      <p className="font-medium">{manager?.first_name} {manager?.last_name}</p>
      {manager?.mobile_phone && (
        <a href={`tel:${manager.mobile_phone}`} className="font-medium">
          {manager.mobile_phone}
        </a>
      )}
      {manager?.email && (
        <a href={`mailto:${manager.email}`} className="font-medium">
          {manager.email}
        </a>
      )}
    </div>
  </div>
);

const Contacts = () => {
  const { me } = useMe();
  const dispatch = useAppDispatch();
  const fetchManagersStatus = useAppSelector(selectUsersStatus);
  const managerId = me.company?.manager;

  // Список менеджеров (group=MANAGER) — может не содержать назначенного
  // пользователю менеджера, если у того нет членства в группе MANAGER
  // (например, админ/стафф). Поэтому ниже отдельным эффектом подтягиваем
  // конкретного пользователя по id.
  useEffect(() => {
    if (fetchManagersStatus === 'idle') {
      dispatch(fetchAllUsers());
    }
  }, [dispatch, fetchManagersStatus]);

  const manager = useAppSelector((state) =>
    managerId
      ? state.users.users.find((user: User) => user.id === managerId)
      : undefined
  );

  // Если менеджер назначен у компании, но не вернулся в общем списке
  // (например, не состоит в группе MANAGER) — загружаем его по id.
  useEffect(() => {
    if (managerId && fetchManagersStatus === 'succeeded' && !manager) {
      dispatch(fetchUserById(managerId));
    }
  }, [dispatch, managerId, manager, fetchManagersStatus]);

  return (
    <div className="w-full bg-white rounded-2xl my-5 pt-[30px] pb-[60px] px-[40px] flex gap-[10px] flex-col justify-between shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
      {/* Заголовок «Контакты» отрисовывается в глобальной шапке. */}
      <div className="flex flex-wrap gap-[10px] py-5 justify-between">
        {manager && (
          <div className="flex flex-col gap-[34px]">
            <p className="text-center bg-[#eff0f3] px-5 py-[10px] font-semibold rounded-xl w-full">
              Менеджер
            </p>
            <div className="h-full flex flex-col justify-between pb-5 gap-[34px]">
              <ManagerBlock manager={manager} label="Контакты:" />
              {manager?.assistant && (
                <ManagerBlock manager={manager.assistant} label="Контакты офис менеджера:" />
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-[34px]">
          <p className="text-center bg-[#eff0f3] px-5 py-[10px] font-semibold rounded-xl w-full">
            Общая контактная информация
          </p>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold">Адрес:</p>
            <p className="font-medium">Республика Татарстан, г. Казань, ул. Дементьева, 2Б корпус 4</p>
            <p className="w-[190px]">Пн-Пт: 08:00 – 17:00 Сб-Вс: Выходные</p>
          </div>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold">Контакты:</p>
            <a href="tel:88007771603" className="font-medium">8 800 777 16 03</a>
            <p>Для звонков из России – бесплатно</p>
            <a href="tel:84951086833" className="font-medium">8 495 108 68 33</a>
            <p>Для звонков из СНГ и других стран</p>
          </div>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold">Наш сайт:</p>
            <a href="https://www.fmeter.ru" className="font-medium">www.fmeter.ru</a>
          </div>
        </div>

        <div className="flex flex-col gap-[34px]">
          <p className="text-center bg-[#eff0f3] px-5 py-[10px] font-semibold rounded-xl w-full">
            Техническая поддержка
          </p>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold">Контакты:</p>
            <a href="tel:88007771603" className="font-medium">8 800 777 16 03</a>
            <p>Добавочная 2 – круглосуточно</p>
          </div>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold">Автоматизированная система заявок:</p>
            <a href="mailto:support@fmeter.ru" className="font-medium">support@fmeter.ru</a>
          </div>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold">Обратиться через мессенджер:</p>
            <div className="mt-[5px] flex gap-5">
              <a href="tg://resolve?domain=Escort_tech_Bot" target="_blank" rel="noreferrer">
                <img src={telegramLogo} alt="Обратиться через Telegram" />
              </a>
              <a href="https://wa.me/79600464665" target="_blank" rel="noreferrer">
                <img src={whatsappLogo} alt="Обратиться через WhatsApp" />
              </a>
              <a href="viber://pa?chatURI=EscortTechSupport/" target="_blank" rel="noreferrer">
                <img src={viberLogo} alt="Обратиться через Viber" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contacts;
